// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A headset on a desktop, for development only (M5-30).
//
// `?xremu=1` installs IWER's emulated runtime as a Quest 3 with tracked hands; `?xremu=controller`
// does the same with Touch controllers. With it, every path in `src/xr/` — session negotiation,
// hand poses, the press gates, the plunger, the placement ritual — runs in an ordinary browser
// tab, which is the difference between finding a bug in five seconds and finding it by putting a
// headset on.
//
// **DEV-gated and tree-shaken, and both words matter.** The gate is `import.meta.env.DEV`, which
// Vite replaces with the literal `false` in a production build; the `if (false)` block is then
// dropped by the minifier along with the `await import('iwer')` inside it, so IWER never appears
// in the bundle. A runtime check on the URL alone would ship the whole emulator to every player
// and — far worse — would leave a query parameter that installs a **fake** `navigator.xr` on the
// production site. A visitor who found it would get an emulated headset instead of their real
// one, and the bug report would say "VR does not work".
//
// The bundle guard is what proves this rather than the comment: `check-bundle-secrets.mjs`
// already reads the built output, and `test/xr/emulation.test.ts` asserts the gate is the
// build-time constant rather than a runtime flag.
//
// **It installs before anything asks for `navigator.xr`.** IWER works by replacing the global, so
// it has to be in place before the session manager reads it. That makes this one of the very few
// things that has to happen at module load, which is why it is a function the entry point awaits
// rather than something lazy.

/** What the query parameter can ask for. */
export type EmulatedInput = 'hand' | 'controller'

/** The parameter name. One place, because the docs, the guard and the code all name it. */
export const EMULATION_PARAM = 'xremu'

/**
 * What the URL is asking for, or undefined.
 *
 * `?xremu=1` and `?xremu=hand` both mean hands — `1` because it is what a developer types and
 * `hand` because it is what it means. Anything else with the parameter present means controllers,
 * deliberately: a typo should still give a working emulator rather than silently nothing, and the
 * two modes are one keystroke apart in the URL bar.
 */
export function requestedEmulation(search: string): EmulatedInput | undefined {
  const params = new URLSearchParams(search)
  if (!params.has(EMULATION_PARAM)) return undefined
  const value = params.get(EMULATION_PARAM)
  return value === '1' || value === '' || value === 'hand' ? 'hand' : 'controller'
}

/**
 * Install the emulated runtime, if this is a development build and the URL asked for it.
 *
 * Resolves to what was installed, or undefined. Never throws: a failed emulator install leaves
 * the page with whatever `navigator.xr` it already had, which on a desktop is nothing, which is
 * exactly the state the page handles anyway.
 */
export async function installEmulatedXR(search: string): Promise<EmulatedInput | undefined> {
  // The build-time constant, first and alone on its own line, so the whole body below is
  // statically unreachable in production and goes away with the import inside it.
  if (!import.meta.env.DEV) return undefined

  const wanted = requestedEmulation(search)
  if (wanted === undefined) return undefined

  try {
    const iwer = await import('iwer')
    const device = new iwer.XRDevice(iwer.metaQuest3, {
      // Hands and controllers both exist on the device; `primaryInputMode` decides which the
      // runtime reports as tracked, which is exactly what `InputMethodTracker` is watching.
      ipd: 0.063,
    })
    // **Forced.** IWER skips installation when `navigator.xr` already exists, which is the right
    // default for a library and exactly wrong here: desktop Chrome exposes a `navigator.xr` that
    // supports no immersive mode at all, so without this the emulator silently declines and
    // `?xremu=1` appears to do nothing. A developer who typed the parameter has asked for the
    // emulated device, and the parameter cannot exist in production (see the note at the top).
    device.installRuntime({ forceInstall: true })
    device.primaryInputMode = wanted
    // Standing at a machine, not at the origin: the placement ritual and the seated detector both
    // read the head's height, and a viewer at y = 0 is a player lying on the floor.
    device.position.set(0, 1.6, 0)
    return wanted
  } catch {
    return undefined
  }
}
